import { Icon } from '@common/components/Icon';
import { Palette } from '@common/theming/Palette';
import React, { CSSProperties, useEffect, useState } from 'react';
import { AssetState } from '../../pages/AssetApp';
import { InjectedWindowContext, PortfolioAsset } from '../../types';
import { TextBlock } from '../TextBlock';

type InfoFloaterProps = {
  info: PortfolioAsset['assetHighlights'];
};

const InfoFloater: React.FC<InfoFloaterProps> = ({ info }) => {
  let keys = Object.keys(info) as Array<keyof PortfolioAsset['assetHighlights']>;

  // If LTV or ARV is in keys, move it to the middle for display reasons, if both exist, remove LTV
  const ltvKey = keys.findIndex((key) => key === 'ltv');
  const ltarvKey = keys.findIndex((key) => key === 'ltarv');

  if (ltarvKey !== -1 || ltvKey !== -1) {
    const filteredKeys = keys.filter((key) => {
      return key !== 'ltv' && key !== 'ltarv';
    }) as Array<keyof PortfolioAsset['assetHighlights']>;
    if (filteredKeys.length > 0) {
      const keysNew = [filteredKeys[0]];
      keysNew.push(ltarvKey !== -1 ? 'ltarv' : 'ltv');
      if (filteredKeys.length > 1) {
        keysNew.push(filteredKeys[1]);
      }
      keys = keysNew;
    }
  }

  const renderValue = (key: keyof PortfolioAsset['assetHighlights'], textAlign: React.CSSProperties['textAlign']) => {
    switch (key) {
      case 'ltv':
        return (
          <>
            <TextBlock size={10} weight={'bold'} style={{ color: Palette.WHITE_50, textAlign }}>
              LTV
            </TextBlock>
            <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', marginTop: 5 }}>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.BLUE,
                  borderRadius: 34,
                  display: 'flex',
                  height: 34,
                  justifyContent: 'center',
                  paddingLeft: 4,
                  width: 34,
                }}>
                <TextBlock size={11} color={Palette.WHITE} weight="bold" style={{ textAlign }}>
                  {info.ltv}
                </TextBlock>
              </div>
            </div>
          </>
        );
      case 'ltarv':
        return (
          <>
            <TextBlock size={10} weight={'bold'} style={{ color: Palette.WHITE_50, textAlign }}>
              LTARV
            </TextBlock>
            <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', marginTop: 5 }}>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.BLUE,
                  borderRadius: 34,
                  display: 'flex',
                  height: 34,
                  justifyContent: 'center',
                  paddingLeft: 4,
                  width: 34,
                }}>
                <TextBlock size={11} color={Palette.WHITE} weight="bold" style={{ textAlign }}>
                  {info.ltarv}
                </TextBlock>
              </div>
            </div>
          </>
        );
      case 'loanAmount':
        return (
          <>
            <TextBlock size={12} weight={'bold'} style={{ color: Palette.WHITE_50, textAlign }}>
              Loan Amount
            </TextBlock>
            <TextBlock weight="bold" color={Palette.WHITE} style={{ marginTop: 8, textAlign }}>
              {info.loanAmount}
            </TextBlock>
          </>
        );
      case 'structure':
        return (
          <>
            <TextBlock size={12} weight={'bold'} style={{ color: Palette.WHITE_50, textAlign }}>
              Structure
            </TextBlock>
            <TextBlock weight="bold" color={Palette.WHITE} style={{ marginTop: 8, textAlign }}>
              {info.structure}
            </TextBlock>
          </>
        );
      case 'strategy':
        return (
          <>
            <TextBlock size={12} weight={'bold'} style={{ color: Palette.WHITE_50, textAlign }}>
              Strategy
            </TextBlock>
            <TextBlock weight="bold" color={Palette.WHITE} style={{ marginTop: 8, textAlign }}>
              {info.strategy}
            </TextBlock>
          </>
        );
      case 'totalCapitalization':
        return (
          <>
            <TextBlock size={12} weight={'bold'} style={{ color: Palette.WHITE_50, textAlign }}>
              Value
            </TextBlock>
            <TextBlock weight="bold" color={Palette.WHITE} style={{ marginTop: 8, textAlign }}>
              {info.totalCapitalization}
            </TextBlock>
          </>
        );
    }
  };

  return (
    <div
      style={{
        backgroundColor: Palette.LIGHT_BLUE_GRAY,
        borderRadius: 15,
        boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
        display: 'flex',
        paddingLeft: 18,
        paddingRight: 18,
      }}>
      {keys.map((highlight, i) => {
        let textAlign: CSSProperties['textAlign'] = 'center';
        if (i === 0) {
          textAlign = 'left';
        }
        if (i === keys.length - 1) {
          textAlign = 'right';
        }
        return (
          <div
            key={`highlight-${i}`}
            style={{
              borderLeft: i === 0 ? undefined : `1px solid ${Palette.BLUE_GRAY}`,
              flex: 1,
              paddingBottom: 10,
              paddingTop: 12,
            }}>
            {renderValue(highlight, textAlign)}
          </div>
        );
      })}
    </div>
  );
};

type HeaderProps = { asset: PortfolioAsset; setCurrentState?: (state: AssetState) => void };

const Header: React.FC<HeaderProps> = ({ asset, setCurrentState }) => {
  const HEADER_IMAGE_HEIGHT = 300;

  const [scrollY, setScrollY] = useState<number>(0);

  const showImageGallery = () => {
    windowContext.ReactNativeWebView?.postMessage('HIDE_EXIT_BUTTON');
    if (setCurrentState) setCurrentState('IMAGE_GALLERY');
  };

  useEffect(() => {
    document.body.onscroll = () => {
      setScrollY(window.scrollY);
    };
    return () => {
      document.body.onscroll = null;
    };
  });

  const windowContext: InjectedWindowContext = window;
  const magnificationFactor = (Math.abs(scrollY) / HEADER_IMAGE_HEIGHT) * 100;
  const headerOpacity = scrollY < 0 ? 1 : 1 - magnificationFactor / 100;
  const imageWidth: number = asset.header.headerImage.file.details.image?.width || 0;

  const { header, assetHighlights } = asset;

  return (
    <div>
      <div
        onClick={() => {
          showImageGallery();
        }}
        style={{
          backgroundImage: `url(${asset.header.headerImage.file.url})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: `${magnificationFactor + 150}%`,
          height: HEADER_IMAGE_HEIGHT,
          opacity: headerOpacity,
          overflow: 'hidden',
          transform: `translateX(-${magnificationFactor * imageWidth})`,
        }}>
        <div
          style={{
            background: 'linear-gradient(180deg, rgba(23, 31, 42, 0) 0%, #171F2A 100%)',
            display: 'flex',
            height: HEADER_IMAGE_HEIGHT,
          }}></div>
      </div>

      <div
        style={{
          flex: 1,
          margin: 24,
          marginTop: -144,
          position: 'relative',
        }}>
        <div
          onClick={() => {
            showImageGallery();
          }}>
          <div
            style={{
              flex: 1,
              opacity: headerOpacity,
            }}>
            <TextBlock weight={'bold'} size={24}>
              {header.title}
            </TextBlock>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ alignItems: 'center', display: 'flex' }}>
                <Icon icon="location-dot" color="whiteAlpha.500" />
                <TextBlock size={16} style={{ marginLeft: 6 }}>
                  {header.locationName}
                </TextBlock>
              </div>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.WHITE,
                  borderRadius: 25,
                  display: 'flex',
                  paddingLeft: 12,
                  paddingRight: 12,
                }}>
                <Icon color="black" boxSize={'12px'} icon="grid" />
                <TextBlock style={{ marginLeft: 5 }} size={12} weight={'bold'} color={Palette.BLACK}>
                  Show all photos
                </TextBlock>
              </div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 24 }}>
          <InfoFloater info={assetHighlights} />
        </div>
      </div>
    </div>
  );
};

export default Header;
